/**
 * @file LegalPdfCard.tsx
 * @description Секция PDF-документа: файл с историей по редакциям, способ
 * показа на сайте и группа (phase19.16, SB139, SB141, SB143;
 * uiux-legal-docs §3).
 *
 * Название, адрес и описание — общие поля `LegalDocFields` (родитель).
 * Новый документ ещё без id: файл выбирается заранее (`pendingFile`) и
 * загружается сразу после создания черновика.
 */

import { LEGAL_PDF_DISPLAY_LABELS, LEGAL_PDF_DISPLAYS, type LegalDocumentDto, type LegalFileInfoDto } from "@chaiberi/shared";
import { Link, useParams } from "react-router";

import { Segmented } from "../site-view/Segmented";
import { LegalPdfFileBlock } from "./LegalPdfFileBlock";
import { LegalRevisionHistory } from "./LegalRevisionHistory";
import type { LegalFileUpload } from "../../hooks/legal/useLegalFileUpload";
import type { LegalDraftValues } from "../../hooks/legal/useLegalDraftForm";
import type { LegalRevisionsState } from "../../hooks/legal/useLegalRevisions";
import { useOpenLegalFile } from "../../hooks/legal/useOpenLegalFile";

interface Props {
  /** Карточка; null — новый документ. */
  doc: LegalDocumentDto | null;
  values: LegalDraftValues;
  onChange: (patch: Partial<LegalDraftValues>) => void;
  upload: LegalFileUpload;
  onPickFile: (file: File) => void;
  onRevertFile?: () => void;
  /** Новый документ: выбранный, но ещё не загруженный файл. */
  pendingFile?: File | null;
  revisions?: LegalRevisionsState;
  disabled?: boolean;
}

const DISPLAY_OPTIONS = LEGAL_PDF_DISPLAYS.map((value) => ({ value, label: LEGAL_PDF_DISPLAY_LABELS[value] }));

export function LegalPdfCard({ doc, values, onChange, upload, onPickFile, onRevertFile, pendingFile, revisions, disabled }: Props) {
  const { slug } = useParams<{ slug: string }>();
  const openFile = useOpenLegalFile();
  const pending: LegalFileInfoDto | null = pendingFile ? { name: pendingFile.name, sizeBytes: pendingFile.size, source: "draft" } : null;
  const file = doc?.file ?? pending;
  const live = doc?.live ?? null;

  return (
    <div className="flex flex-col gap-5">
      <LegalPdfFileBlock
        file={file}
        live={live}
        upload={upload}
        onPick={onPickFile}
        onOpen={doc && doc.file ? () => openFile(doc.id, doc.file?.source === "draft" || !live ? "draft" : live.number) : undefined}
        onRevert={onRevertFile}
        disabled={disabled}
      >
        {doc && revisions && live ? (
          <div className="flex flex-col gap-1 pt-1">
            <span className="type-caption text-on-surface-variant">История файлов по редакциям</span>
            <LegalRevisionHistory docId={doc.id} kind="PDF" revisions={revisions} liveNumber={live.number} />
          </div>
        ) : null}
      </LegalPdfFileBlock>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-on-surface">На сайте</span>
        <Segmented
          aria-label="Как показывать PDF на сайте"
          options={DISPLAY_OPTIONS.map((o) => ({ ...o, disabled }))}
          value={values.pdfDisplay}
          onChange={(pdfDisplay) => onChange({ pdfDisplay })}
        />
        <p className="type-caption text-on-surface-variant">
          На телефоне покупатель всегда видит кнопку «Открыть PDF» — встроенный просмотр там работает плохо.
        </p>
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-on-surface">Группа</span>
        {doc?.group ? (
          <Link
            to={`/shop/${slug}/settings/legal-documents/${doc.group.id}`}
            className="type-body inline-flex min-h-12 items-center font-medium text-primary hover:underline"
          >
            {doc.group.title} →
          </Link>
        ) : (
          <p className="type-caption text-on-surface-variant">Без группы. Добавить документ в группу можно в карточке группы.</p>
        )}
      </div>
    </div>
  );
}
